import React, { useCallback, useEffect, useRef, useState } from "react";
import { Upload, FileText, Loader2, CheckCircle2, XCircle } from "lucide-react";
import {
  createResearchImport,
  uploadReleaseFile,
  validateResearchImport,
  commitResearchImport,
  getResearchImport,
} from "../../../Services/adminResearchLibrary";
import ConfirmDialog from "../../../Components/admin/shared/ConfirmDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { ImportStatusBadge } from "./badges";
import { ImportReport, CommitSummary } from "./ImportReport";
import {
  IMPORT_STATUS,
  POLL_INTERVAL_MS,
  RELEASE_VERSION_RE,
  canCommit,
  errorMessage,
  formatBytes,
  inferReleaseVersion,
  isCommitTerminal,
  isValidationTerminal,
  validateReleaseFiles,
} from "./researchLibraryFormat";

const StepHeader = ({ step, title }) => (
  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
    Step {step} of 3 · {title}
  </p>
);

const FileProgress = ({ file, state }) => {
  const pct = Math.round((state?.progress || 0) * 100);
  return (
    <li className="rounded-md border px-3 py-2 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2">
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate font-mono text-xs">{file.name}</span>
        </span>
        <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
          {formatBytes(file.size)}
          {state?.status === "done" && (
            <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-label="Uploaded" />
          )}
          {state?.status === "error" && (
            <XCircle className="h-4 w-4 text-destructive" aria-label="Upload failed" />
          )}
        </span>
      </div>
      {state && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-muted">
          <div
            className={
              state.status === "error" ? "h-full bg-destructive" : "h-full bg-primary"
            }
            style={{ width: `${state.status === "error" ? 100 : pct}%` }}
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${file.name} upload`}
          />
        </div>
      )}
      {state?.error && <p className="mt-1 text-xs text-destructive">{state.error}</p>}
    </li>
  );
};

/**
 * Import a Research Dialogue Library release: choose files → upload to S3 →
 * validate (dry run, polled) → review the report → commit (polled).
 * Pass `existingImport` to resume tracking an import from the recent list.
 */
const ImportReleaseModal = ({ open, onOpenChange, existingImport, onChanged }) => {
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const genRef = useRef(0);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  const [files, setFiles] = useState([]);
  const [releaseVersion, setReleaseVersion] = useState("");
  const [fileErrors, setFileErrors] = useState([]);
  const [importDoc, setImportDoc] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [progress, setProgress] = useState({});
  const [stage, setStage] = useState("select"); // select | upload | track
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState(null);
  const [embed, setEmbed] = useState(true);
  const [confirmCommit, setConfirmCommit] = useState(false);

  const stopPolling = useCallback(() => {
    genRef.current += 1;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setPolling(false);
  }, []);

  const poll = useCallback(
    (importId, isDone, onDone) => {
      stopPolling();
      const gen = genRef.current;
      setPolling(true);
      const tick = async () => {
        if (gen !== genRef.current) return;
        try {
          const data = await getResearchImport(importId);
          if (gen !== genRef.current) return;
          const doc = data?.import;
          if (doc) setImportDoc(doc);
          if (doc && isDone(doc.status)) {
            setPolling(false);
            onDone?.(doc);
            return;
          }
        } catch (err) {
          if (gen !== genRef.current) return;
          setError(errorMessage(err, "Could not read the import status"));
          setPolling(false);
          return;
        }
        timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
      };
      timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    },
    [stopPolling]
  );

  const notifyChanged = useCallback(() => onChangedRef.current?.(), []);

  const track = useCallback(
    (doc) => {
      if (!doc?.import_id) return;
      setError(null);
      if (doc.status === IMPORT_STATUS.VALIDATING) {
        poll(doc.import_id, isValidationTerminal);
      } else if (doc.status === IMPORT_STATUS.COMMITTING) {
        poll(doc.import_id, isCommitTerminal, notifyChanged);
      }
    },
    [poll, notifyChanged]
  );

  const reset = useCallback(() => {
    stopPolling();
    setFiles([]);
    setReleaseVersion("");
    setFileErrors([]);
    setImportDoc(null);
    setUploads([]);
    setProgress({});
    setStage("select");
    setError(null);
    setEmbed(true);
    if (inputRef.current) inputRef.current.value = "";
  }, [stopPolling]);

  // Resume an existing import when opened from the recent-imports list.
  useEffect(() => {
    if (!open || !existingImport) return;
    setImportDoc(existingImport);
    setStage("track");
    track(existingImport);
  }, [open, existingImport, track]);

  // Cancel polling on unmount.
  useEffect(() => stopPolling, [stopPolling]);

  const handleClose = (next) => {
    if (busy) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const handleFileChange = (e) => {
    const chosen = Array.from(e.target.files || []);
    const { errors } = validateReleaseFiles(chosen);
    setFiles(chosen);
    setFileErrors(chosen.length ? errors : []);
    setError(null);
    if (!releaseVersion) {
      const inferred = inferReleaseVersion(chosen.map((f) => f.name));
      if (inferred) setReleaseVersion(inferred);
    }
  };

  const versionError =
    releaseVersion && !RELEASE_VERSION_RE.test(releaseVersion.trim())
      ? "Release version looks like v26.7."
      : null;

  const uploadAll = async (doc, uploadList, only = null) => {
    const targets = files.filter((f) => !only || only.includes(f.name));
    const results = await Promise.all(
      targets.map(async (file) => {
        const upload = uploadList.find((u) => u.file === file.name);
        if (!upload?.url) {
          setProgress((p) => ({
            ...p,
            [file.name]: { status: "error", progress: 0, error: "No upload URL returned." },
          }));
          return false;
        }
        setProgress((p) => ({ ...p, [file.name]: { status: "uploading", progress: 0 } }));
        try {
          await uploadReleaseFile(upload, file, (fraction) =>
            setProgress((p) => ({
              ...p,
              [file.name]: { status: "uploading", progress: fraction },
            }))
          );
          setProgress((p) => ({ ...p, [file.name]: { status: "done", progress: 1 } }));
          return true;
        } catch (err) {
          setProgress((p) => ({
            ...p,
            [file.name]: {
              status: "error",
              progress: 0,
              error: errorMessage(err, "Upload failed"),
            },
          }));
          return false;
        }
      })
    );
    if (results.some((ok) => !ok)) {
      setError("Some files failed to upload. Retry them before validating.");
      return;
    }
    const validated = await validateResearchImport(doc.import_id);
    const next = validated?.import || { ...doc, status: IMPORT_STATUS.VALIDATING };
    setImportDoc(next);
    setStage("track");
    if (isValidationTerminal(next.status)) return;
    poll(next.import_id, isValidationTerminal);
  };

  const handleStart = async () => {
    const { ok, errors } = validateReleaseFiles(files);
    if (!ok || versionError) {
      setFileErrors(errors);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await createResearchImport({
        files: files.map((f) => f.name),
        release_version: releaseVersion.trim() || undefined,
      });
      const doc = created?.import;
      const list = created?.uploads || [];
      if (!doc?.import_id) throw new Error("The import was not created.");
      setImportDoc(doc);
      setUploads(list);
      setStage("upload");
      await uploadAll(doc, list);
    } catch (err) {
      setError(errorMessage(err, "Import failed to start"));
    } finally {
      setBusy(false);
    }
  };

  const handleRetryUploads = async () => {
    const failed = Object.entries(progress)
      .filter(([, s]) => s.status === "error")
      .map(([name]) => name);
    setBusy(true);
    setError(null);
    try {
      await uploadAll(importDoc, uploads, failed);
    } catch (err) {
      setError(errorMessage(err, "Validation failed to start"));
    } finally {
      setBusy(false);
    }
  };

  const handleCommit = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await commitResearchImport(importDoc.import_id, { embed });
      const next = data?.import || { ...importDoc, status: IMPORT_STATUS.COMMITTING };
      setImportDoc(next);
      if (isCommitTerminal(next.status)) {
        notifyChanged();
      } else {
        poll(next.import_id, isCommitTerminal, notifyChanged);
      }
    } catch (err) {
      setError(errorMessage(err, "Commit failed to start"));
    } finally {
      setBusy(false);
    }
  };

  const status = importDoc?.status;
  const hasFailedUploads = Object.values(progress).some((s) => s.status === "error");
  const showReport =
    stage === "track" &&
    importDoc?.report &&
    [IMPORT_STATUS.VALIDATED, IMPORT_STATUS.INVALID, IMPORT_STATUS.FAILED].includes(status);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => busy && e.preventDefault()}
        onEscapeKeyDown={(e) => busy && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Import library release</DialogTitle>
          <DialogDescription>
            Upload the release's Aris_Dialogues_…_v26.x CSV files (.csv or .csv.gz),
            validate them as a dry run, then commit.
          </DialogDescription>
        </DialogHeader>

        {importDoc && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="font-mono">{importDoc.import_id}</span>
            <ImportStatusBadge status={status} />
            {importDoc.release_version && <span>{importDoc.release_version}</span>}
            {polling && (
              <span className="inline-flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> checking every{" "}
                {POLL_INTERVAL_MS / 1000}s
              </span>
            )}
          </div>
        )}

        <div className="space-y-4">
          {stage === "select" && (
            <>
              <StepHeader step={1} title="Choose files" />
              <label className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-input bg-background px-6 py-8 cursor-pointer hover:bg-muted/30 transition">
                <Upload className="h-6 w-6 text-muted-foreground" />
                <span className="text-sm font-medium">
                  {files.length
                    ? `${files.length} file${files.length === 1 ? "" : "s"} selected`
                    : "Click to choose the release files"}
                </span>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept=".csv,.gz"
                  className="hidden"
                  data-testid="release-files"
                  onChange={handleFileChange}
                />
              </label>
              {files.length > 0 && (
                <ul className="space-y-1">
                  {files.map((f) => (
                    <FileProgress key={f.name} file={f} state={null} />
                  ))}
                </ul>
              )}
              <div className="grid gap-2">
                <Label htmlFor="release-version">Release version (optional)</Label>
                <Input
                  id="release-version"
                  placeholder="v26.7"
                  value={releaseVersion}
                  onChange={(e) => setReleaseVersion(e.target.value)}
                  className="max-w-[200px]"
                />
                {versionError && (
                  <p className="text-xs text-destructive">{versionError}</p>
                )}
              </div>
              {fileErrors.length > 0 && (
                <ul className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive list-disc pl-8">
                  {fileErrors.map((msg) => (
                    <li key={msg}>{msg}</li>
                  ))}
                </ul>
              )}
            </>
          )}

          {stage === "upload" && (
            <>
              <StepHeader step={2} title="Upload" />
              <ul className="space-y-2">
                {files.map((f) => (
                  <FileProgress
                    key={f.name}
                    file={f}
                    state={progress[f.name] || { status: "pending", progress: 0 }}
                  />
                ))}
              </ul>
            </>
          )}

          {stage === "track" && (
            <>
              <StepHeader
                step={3}
                title={
                  status === IMPORT_STATUS.COMMITTED || status === IMPORT_STATUS.COMMITTING
                    ? "Commit"
                    : "Validate"
                }
              />
              {status === IMPORT_STATUS.AWAITING_UPLOAD && (
                <p className="text-sm text-muted-foreground">
                  This import's files were never uploaded. Start a new import.
                </p>
              )}
              {status === IMPORT_STATUS.VALIDATING && (
                <p className="flex items-center gap-2 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> Validating the release
                  (integrity, contract and release checks)…
                </p>
              )}
              {status === IMPORT_STATUS.COMMITTING && (
                <p className="flex items-center gap-2 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> Committing the release…
                </p>
              )}
              {status === IMPORT_STATUS.FAILED && importDoc?.error && (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  {typeof importDoc.error === "string"
                    ? importDoc.error
                    : JSON.stringify(importDoc.error)}
                </div>
              )}
              {status === IMPORT_STATUS.COMMITTED && (
                <>
                  <p className="flex items-center gap-2 text-sm font-medium text-emerald-700">
                    <CheckCircle2 className="h-4 w-4" /> Release committed.
                  </p>
                  <CommitSummary summary={importDoc?.commit_summary} />
                </>
              )}
              {showReport && <ImportReport report={importDoc.report} />}
              {status === IMPORT_STATUS.VALIDATED && canCommit(importDoc) && (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={embed}
                    onChange={(e) => setEmbed(e.target.checked)}
                  />
                  Generate embeddings for attachable dialogues
                </label>
              )}
            </>
          )}

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={busy}>
            {stage === "select" ? "Cancel" : "Close"}
          </Button>
          {stage === "select" && (
            <Button
              onClick={handleStart}
              disabled={busy || files.length === 0 || fileErrors.length > 0 || !!versionError}
            >
              {busy ? "Starting…" : "Upload & validate"}
            </Button>
          )}
          {stage === "upload" && hasFailedUploads && (
            <Button onClick={handleRetryUploads} disabled={busy}>
              {busy ? "Retrying…" : "Retry failed uploads"}
            </Button>
          )}
          {stage === "upload" && !hasFailedUploads && !busy && error && (
            <Button onClick={handleRetryUploads}>Validate</Button>
          )}
          {stage === "track" && !polling && error && importDoc && (
            <Button variant="outline" onClick={() => track(importDoc)} disabled={busy}>
              Check again
            </Button>
          )}
          {stage === "track" && status === IMPORT_STATUS.VALIDATED && (
            <Button
              onClick={() => setConfirmCommit(true)}
              disabled={busy || !canCommit(importDoc)}
            >
              Commit release
            </Button>
          )}
        </DialogFooter>

        <ConfirmDialog
          open={confirmCommit}
          onOpenChange={setConfirmCommit}
          title={`Commit ${importDoc?.report?.library_version || "this release"}?`}
          description="Committing supersedes the current library rows with this release. Admin overrides (titles, descriptions, holds) are kept."
          confirmLabel="Commit release"
          onConfirm={handleCommit}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ImportReleaseModal;
